import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useCultivation } from '../contexts/CultivationContext';

const UserDashboardPage = () => {
  const { user, meritPoints, updateProfile } = useUser();
  const { 
    cultivation, 
    currentLevel, 
    nextLevel, 
    getCultivationProgress, 
    CULTIVATION_SKILLS,
    practiceSkill 
  } = useCultivation();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [profileMode, setProfileMode] = useState('view');
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    bio: user?.bio || '',
  });
  
  // 如果用户未登录，重定向到登录页面
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
  };
  
  const handleProfileSubmit = (e) => {
    e.preventDefault();
    updateProfile(profileData);
    setProfileMode('view');
  };
  
  const handlePracticeSkill = (skillId) => {
    const result = practiceSkill(skillId);
    if (result) {
      // 可以添加练习成功的反馈
      console.log('练习成功', result);
    }
  };
  
  // 任务数据（示例）
  const tasks = [
    { id: 1, title: '每日冥想', description: '完成15分钟冥想', reward: 10, status: 'completed' },
    { id: 2, title: '筑基丹炼制', description: '学习炼制筑基丹的基本步骤', reward: 20, status: 'in-progress' },
    { id: 3, title: '道友互助', description: '帮助3位道友解决修炼问题', reward: 15, status: 'not-started' },
    { id: 4, title: '灵气感知', description: '完成基础灵气感知训练', reward: 10, status: 'completed' },
  ];
  
  return (
    <div className="space-y-6">
      <section className="cyber-panel p-6 rounded-lg">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-4 mb-4 md:mb-0">
            <div className="w-20 h-20 rounded-full border-2 border-cyber-primary overflow-hidden">
              <img 
                src={user.avatar || `https://randomuser.me/api/portraits/men/32.jpg`} 
                alt="用户头像" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-2xl font-cyber text-cyber-primary">{user.displayName}</h1>
              <p className="text-cyber-light">加入时间: {new Date(user.joinDate).toLocaleDateString()}</p>
            </div>
          </div>
          
          <div className="flex flex-col text-right">
            <div className="mb-2">
              <span className="text-cyber-secondary">当前境界:</span>
              <span className="ml-2 text-cyber-accent font-cyber">{currentLevel?.name || '筑基期'}</span>
            </div>
            <div className="mb-2">
              <span className="text-cyber-secondary">功德点:</span>
              <span className="ml-2 text-cyber-accent font-cyber">{meritPoints}</span>
            </div>
            <div>
              <Link to="/cultivation" className="cyber-button inline-block">
                继续修炼
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* 标签导航 */}
      <div className="border-b border-cyber-primary">
        <nav className="flex flex-wrap -mb-px">
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            修炼概览
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('tasks')}
          >
            任务列表
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'inventory'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('inventory')}
          >
            道具背包
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('profile')}
          >
            个人资料
          </button>
        </nav>
      </div>
      
      {/* 标签内容 */}
      <div className="tab-content">
        {/* 修炼概览 */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* 修炼进度 */}
            <section className="cyber-panel p-6 rounded-lg">
              <h2 className="text-xl font-cyber text-cyber-primary mb-4">修炼进度</h2>
              
              <div className="mb-4">
                <div className="flex justify-between mb-1">
                  <span className="text-cyber-light">{currentLevel?.name || '筑基期'}</span>
                  <span className="text-cyber-light">{nextLevel?.name || '飞升期'}</span>
                </div>
                <div className="cultivation-progress">
                  <div 
                    className="cultivation-progress-bar" 
                    style={{ width: `${getCultivationProgress()}%` }}
                  ></div>
                </div>
                <div className="text-right mt-1">
                  <span className="text-cyber-light text-sm">
                    {getCultivationProgress()}% 完成
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="cyber-panel p-4 rounded">
                  <div className="text-center">
                    <div className="text-3xl text-cyber-accent mb-2">{cultivation.experience}</div>
                    <div className="text-cyber-light text-sm">总修为</div>
                  </div>
                </div>
                <div className="cyber-panel p-4 rounded">
                  <div className="text-center">
                    <div className="text-3xl text-cyber-accent mb-2">
                      {nextLevel ? nextLevel.requiredExp - cultivation.experience : '已达顶级'}
                    </div>
                    <div className="text-cyber-light text-sm">距离突破</div>
                  </div>
                </div>
                <div className="cyber-panel p-4 rounded">
                  <div className="text-center">
                    <div className="text-3xl text-cyber-accent mb-2">
                      {Object.keys(cultivation.skills).length}
                    </div>
                    <div className="text-cyber-light text-sm">已修炼技能</div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* 技能训练 */}
            <section className="cyber-panel p-6 rounded-lg">
              <h2 className="text-xl font-cyber text-cyber-primary mb-4">技能训练</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CULTIVATION_SKILLS.slice(0, 4).map((skill) => (
                  <div key={skill.id} className="cultivation-item flex justify-between items-center">
                    <div>
                      <h3 className="text-cyber-secondary">{skill.name}</h3>
                      <p className="text-cyber-light text-sm">{skill.description}</p>
                      <div className="mt-2 text-xs">
                        <span className="text-cyber-accent">经验值: </span>
                        <span className="text-cyber-light">
                          {cultivation.skills[skill.id] || 0}
                        </span>
                      </div>
                    </div>
                    <button
                      className="cyber-button"
                      onClick={() => handlePracticeSkill(skill.id)}
                    >
                      练习
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-4">
                <Link to="/cultivation" className="cyber-button">
                  查看所有技能
                </Link>
              </div>
            </section>
            
            {/* 个人属性 */}
            <section className="cyber-panel p-6 rounded-lg">
              <h2 className="text-xl font-cyber text-cyber-primary mb-4">个人属性</h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(cultivation.attributes).map(([key, value]) => (
                  <div key={key} className="text-center p-2">
                    <div className="text-xl text-cyber-accent mb-1">{value}</div>
                    <div className="text-cyber-light text-sm">
                      {key === 'constitution' && '体质'}
                      {key === 'spirit' && '精神'}
                      {key === 'control' && '控制'}
                      {key === 'strength' && '力量'}
                      {key === 'intelligence' && '智力'}
                      {key === 'dexterity' && '敏捷'}
                      {key === 'perception' && '感知'}
                      {key === 'wisdom' && '智慧'}
                      {key === 'technology' && '科技'}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
        
        {/* 任务列表 */}
        {activeTab === 'tasks' && (
          <div className="cyber-panel p-6 rounded-lg">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-cyber text-cyber-primary">任务列表</h2>
              <Link to="/quest-system" className="cyber-button">
                查找新任务
              </Link>
            </div>
            
            <div className="space-y-4">
              {tasks.map((task) => (
                <div key={task.id} className="border border-cyber-primary rounded-lg p-4 bg-cyber-dark bg-opacity-50">
                  <div className="flex justify-between">
                    <h3 className="text-lg text-cyber-secondary">{task.title}</h3>
                    <div className="text-cyber-accent">
                      奖励: {task.reward} <i className="fas fa-coins ml-1"></i>
                    </div>
                  </div>
                  <p className="text-cyber-light my-2">{task.description}</p>
                  <div className="flex justify-between items-center mt-2">
                    <div>
                      {task.status === 'completed' && (
                        <span className="text-green-500 text-sm">
                          <i className="fas fa-check-circle mr-1"></i> 已完成
                        </span>
                      )}
                      {task.status === 'in-progress' && (
                        <span className="text-blue-500 text-sm">
                          <i className="fas fa-spinner fa-spin mr-1"></i> 进行中
                        </span>
                      )}
                      {task.status === 'not-started' && (
                        <span className="text-gray-400 text-sm">
                          <i className="fas fa-clock mr-1"></i> 未开始
                        </span>
                      )}
                    </div>
                    <button className="cyber-button text-sm">
                      {task.status === 'completed' ? '已领取' : '查看详情'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* 道具背包 */}
        {activeTab === 'inventory' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-xl font-cyber text-cyber-primary mb-6">道具背包</h2>
            
            {/* 道具类别 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 丹药 */}
              <div className="cyber-panel p-4 rounded">
                <h3 className="text-lg text-cyber-secondary mb-3 border-b border-cyber-primary pb-2">丹药</h3>
                <div className="text-center py-8">
                  <p className="text-cyber-light">暂无丹药</p>
                </div>
              </div>
              
              {/* 法器 */}
              <div className="cyber-panel p-4 rounded">
                <h3 className="text-lg text-cyber-secondary mb-3 border-b border-cyber-primary pb-2">法器</h3>
                <div className="text-center py-8">
                  <p className="text-cyber-light">暂无法器</p>
                </div>
              </div>
              
              {/* 符箓 */}
              <div className="cyber-panel p-4 rounded">
                <h3 className="text-lg text-cyber-secondary mb-3 border-b border-cyber-primary pb-2">符箓</h3>
                <div className="text-center py-8">
                  <p className="text-cyber-light">暂无符箓</p>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-6">
              <Link to="/virtual-currency" className="cyber-button">
                前往宗门商店
              </Link>
            </div>
          </div>
        )}
        
        {/* 个人资料 */}
        {activeTab === 'profile' && (
          <div className="cyber-panel p-6 rounded-lg">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-cyber text-cyber-primary">个人资料</h2>
              {profileMode === 'view' ? (
                <button
                  className="cyber-button"
                  onClick={() => setProfileMode('edit')}
                >
                  编辑资料
                </button>
              ) : (
                <button
                  className="cyber-button"
                  onClick={() => setProfileMode('view')}
                >
                  取消编辑
                </button>
              )}
            </div>
            
            {profileMode === 'view' ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-cyber-secondary">道号</h3>
                  <p className="text-cyber-light">{user.displayName}</p>
                </div>
                <div>
                  <h3 className="text-cyber-secondary">用户名</h3>
                  <p className="text-cyber-light">{user.username}</p>
                </div>
                <div>
                  <h3 className="text-cyber-secondary">电子邮箱</h3>
                  <p className="text-cyber-light">{user.email}</p>
                </div>
                <div>
                  <h3 className="text-cyber-secondary">个人简介</h3>
                  <p className="text-cyber-light">{user.bio || '这位道友还没有留下简介'}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleProfileSubmit}>
                <div className="mb-4">
                  <label htmlFor="displayName" className="block text-cyber-secondary mb-2">
                    道号
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={profileData.displayName}
                    onChange={handleProfileChange}
                    className="w-full bg-cyber-dark border border-cyber-primary text-cyber-light px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyber-accent"
                  />
                </div>
                <div className="mb-6">
                  <label htmlFor="bio" className="block text-cyber-secondary mb-2">
                    个人简介
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={profileData.bio}
                    onChange={handleProfileChange}
                    rows="4"
                    className="w-full bg-cyber-dark border border-cyber-primary text-cyber-light px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyber-accent"
                  ></textarea>
                </div>
                <button type="submit" className="cyber-button">
                  保存更改
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboardPage;
